import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Button,
  Flex,
  Form,
  FormInstance,
  FormItemProps,
  Modal,
  Popconfirm,
  Table,
} from "antd";
import { ColumnType } from "antd/es/table/interface";
import React, {
  cloneElement,
  forwardRef,
  ReactElement,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { AddHandler, ContextType, DataType } from "../contexts/DataProvider";
import { Filter } from "../shared/protocols/editor/PtlDataFind";
import { IdRender, Important } from "./common";

interface Props<T extends DataType> {
  columns: EditableColumnType<T>[];
  context: ContextType<T>;
  editingData?: T;
  editingDataPreserve?: boolean;
  filter?: Filter;
  title?: (data: readonly T[]) => React.ReactNode;
  sortable?: boolean;
  onSort?: (targetId: T["_id"], droppedId?: T["_id"]) => void;
  onEditingDataChange?: (prevData: T, data: T) => void;
}

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  "data-row-key": string;
  "allow-sorting"?: string;
}

interface CellProps<T> extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  editor: React.ReactNode;
  record: T;
  index: number;
  formItemProps: FormItemProps<any>;
}

export interface EditableColumnType<T extends DataType> extends ColumnType<T> {
  editor?: React.ReactNode;
  formItemProps?: FormItemProps<any>;
}

export interface AdvancedTableRef<T extends DataType> {
  add: (values: Omit<T, "_id"> | Omit<T, "_id">[]) => Promise<void>;
  editSubmit: () => Promise<void>;
  delete: (id: T["_id"]) => Promise<void>;
}

const Container = styled.div`
  height: 100%;
  width: 100%;
  min-width: 0;
  overflow: hidden;

  .ant-table-wrapper {
    height: 100%;
  }

  .ant-table-title > .ant-flex {
    flex-wrap: wrap;
    row-gap: 8px;
  }
`;

const AdvancedTable = forwardRef(AdvancedTableInner) as <T extends DataType>(
  props: Props<T> & { ref?: React.Ref<AdvancedTableRef<T>> },
) => React.ReactElement;

export default AdvancedTable;

function AdvancedTableInner<T extends DataType>(
  {
    columns,
    context,
    editingData,
    editingDataPreserve = true,
    filter,
    sortable,
    title,
    onSort,
    onEditingDataChange,
  }: Props<T>,
  ref: React.Ref<AdvancedTableRef<T>>,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<Filter>(filter || {});
  const [_filter, _setFilter] = useState<Filter>({});
  const [scroll, setScroll] = useState({ x: 800, y: 500 });
  const [form] = Form.useForm();

  const { data, load, update, add: _add, remove } = context;

  useImperativeHandle(ref, () => ({
    add: async (values) => {
      setLoading(true);
      try {
        await _add(values);
      } catch (e) {
      } finally {
        setLoading(false);
      }
    },
    editSubmit: async () => {
      setLoading(true);
      try {
        const value = await form.validateFields();
        if (editingData?._id || editingData?._id === 0)
          await update({ _id: editingData._id }, { $set: value });
      } catch (e) {
      } finally {
        setLoading(false);
      }
    },
    delete: async (id) => {
      setLoading(true);
      await remove(id);
      setLoading(false);
    },
  }));

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(editingData);
  }, [editingData]);

  useEffect(() => {
    const value = { ...filter };
    for (let k in _filter) {
      if (value[k]) value[k] = { $and: [_filter[k], value[k]] } as any;
      else value[k] = _filter[k];
    }
    setFilters(value);
  }, [JSON.stringify(filter), JSON.stringify(_filter)]);

  useEffect(() => {
    _load(true);
  }, [JSON.stringify(filters || {})]);

  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const tableEl = entry.target as HTMLDivElement;
        const headerEl = tableEl.querySelector(
          ".ant-table-thead",
        ) as HTMLTableSectionElement | null;
        const titleEl = tableEl.querySelector(
          ".ant-table-title",
        ) as HTMLDivElement | null;
        setScroll({
          x: Math.max(entry.contentRect.width, 960),
          y: Math.max(
            180,
            entry.contentRect.height -
              (headerEl?.offsetHeight ?? 0) -
              (titleEl?.offsetHeight ?? 0),
          ),
        });
      }
    });
    containerRef.current && ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const _load = async (reset?: boolean) => {
    setLoading(true);
    await load(filters, reset);
    setLoading(false);
  };

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (loading) return;
    const target = e.target as HTMLDivElement;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 50)
      _load();
  };

  const Row = React.forwardRef<HTMLDivElement, RowProps>((props, ref) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: props["data-row-key"] });

    const style: React.CSSProperties = {
      ...props.style,
      transform: CSS.Translate.toString(transform),
      transition,
      cursor: sortable ? "move" : "default",
      ...(isDragging ? { position: "relative", zIndex: 3 } : {}),
    };

    return (
      <div
        {...props}
        ref={(node) => {
          setNodeRef(node);
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            (ref as React.MutableRefObject<HTMLDivElement | null>).current =
              node;
          }
        }}
        style={style}
        {...attributes}
        {...listeners}
      />
    );
  });

  const Cell = ({
    editing,
    dataIndex,
    title,
    editor,
    record,
    index,
    children,
    formItemProps,
    ...restProps
  }: CellProps<T>) => {
    return (
      <div {...restProps}>
        {editing ? (
          <Form.Item {...formItemProps} name={dataIndex} style={{ margin: 0 }}>
            {editor}
          </Form.Item>
        ) : (
          children
        )}
      </div>
    );
  };

  const mergedColumns: ColumnType<T>[] = columns!.map((column) => {
    if (!column.editor) return column;
    return {
      ...column,
      onCell: (record: T) => ({
        record,
        editor: column.editor,
        dataIndex: column.dataIndex,
        title: column.title,
        editing: editingData?._id === record._id,
        formItemProps: column.formItemProps,
      }),
    } as ColumnType<T>;
  });

  const getTable = () => (
    <Table<T>
      columns={mergedColumns}
      components={{ body: { row: Row, cell: Cell } }}
      dataSource={data}
      loading={loading}
      pagination={false}
      rowKey="_id"
      scroll={scroll}
      title={title}
      virtual
      onChange={(_, filters) => {
        const value: Filter = {};
        for (let k in filters)
          if ((filters[k]?.length ?? 0) > 0)
            value[k] = { $in: filters[k] } as any;
        _setFilter(value);
      }}
      onScroll={onScroll}
    />
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 1 } }),
  );

  return (
    <Container ref={containerRef}>
      <Form
        form={form}
        component={false}
        onValuesChange={onEditingDataChange}
        preserve={editingDataPreserve}
      >
        {sortable ? (
          <DndContext
            sensors={sensors}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={(e) => {
              onSort?.(e.active.id, e.over?.id);
            }}
          >
            <SortableContext
              items={data.map((item) => item._id)}
              strategy={verticalListSortingStrategy}
            >
              {getTable()}
            </SortableContext>
          </DndContext>
        ) : (
          getTable()
        )}
      </Form>
    </Container>
  );
}

export function getIdColumn<T extends DataType>(): EditableColumnType<T> {
  return {
    title: "序号",
    width: 60,
    render: (_: any, __: T, index: number) => <IdRender>{index + 1}</IdRender>,
  };
}

export function getActionColumns<T extends DataType>({
  width = 150,
  editingData,
  setEditingData,
  display,
  prefix,
  editSubmit,
  $delete,
}: {
  width?: number;
  editingData: T | undefined;
  setEditingData: (data: T | undefined) => void;
  display?: (data: T | undefined) => ReactNode;
  prefix?: (data: T | undefined) => ReactNode;
  editSubmit?: () => AdvancedTableRef<T>["editSubmit"] | undefined;
  $delete?: () => AdvancedTableRef<T>["delete"] | undefined;
}) {
  return {
    title: "操作",
    width,
    render: (_: any, record: T, __: number) => {
      const editable = record._id === editingData?._id;
      return editable ? (
        <Flex gap={10}>
          <Button
            color="primary"
            variant="link"
            onClick={async () => {
              await editSubmit?.()?.();
              setEditingData(undefined);
            }}
          >
            <CheckOutlined />
          </Button>
          <Button
            color="primary"
            variant="link"
            onClick={() => {
              setEditingData(undefined);
            }}
          >
            <CloseOutlined />
          </Button>
        </Flex>
      ) : (
        <Flex gap={10}>
          {prefix && prefix(record)}
          <Button
            disabled={!!editingData}
            color="primary"
            variant="link"
            onClick={() => {
              setEditingData({ ...record });
            }}
          >
            <EditOutlined />
          </Button>
          <Popconfirm
            title={
              <p>
                确认删除
                <Important>
                  {display?.(record) || record.name || record._id}
                </Important>
              </p>
            }
            okText="确定"
            cancelText="取消"
            onConfirm={async () => $delete?.()?.(record._id)}
          >
            <Button disabled={!!editingData} color="danger" variant="link">
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        </Flex>
      );
    },
  };
}

export function AddButton<T extends DataType>({
  add,
  createValue,
  title,
  children,
  onValuesChange,
}: {
  add?: () => AddHandler<T> | undefined;
  createValue?: () => Partial<Omit<T, "_id">>;
  title: ReactNode;
  children: ReactNode;
  onValuesChange?: (changed: Partial<T>, all: T, form: FormInstance<T>) => void;
}) {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        onClick={() => {
          form.resetFields();
          form.setFieldsValue(createValue?.() || {});
          setOpen(true);
        }}
      >
        <PlusCircleOutlined />
        新增
      </Button>
      <Modal
        open={open}
        centered
        closable={false}
        maskClosable={false}
        title={title}
        okText="确定"
        cancelText="取消"
        onOk={() => form.submit()}
        onCancel={() => setOpen(false)}
      >
        <Form
          form={form}
          labelAlign="left"
          labelCol={{ span: 6 }}
          onFinish={async (values) => {
            await add?.()?.(values);
            setOpen(false);
          }}
          onValuesChange={(changed, all) =>
            onValuesChange?.(changed, all, form)
          }
        >
          {children}{" "}
        </Form>
      </Modal>
    </>
  );
}

export function ColumnTitle({
  title,
  children,
}: {
  title: ReactNode;
  children?: ReactNode;
}) {
  return (
    <Flex justify="space-between" align="center" gap={6}>
      <span style={{ flex: 1 }}>{title}</span>
      {children}
    </Flex>
  );
}
